import { FC, useCallback, useMemo, useState } from 'react';
import { NftVideoMedia } from 'components/NftVideoMedia';
import { OpenOriginalButton } from 'components/UI/OpenOriginalButton';
import { BlockchainBadge } from './__partials/blockchainBadge';
import { InReviewBadge } from './__partials/InReviewBadge';
import { RejectedBadge } from './__partials/RejectedBadge';
import { SoldBadge } from './__partials/SoldBadge';
import { TypeBadge } from './__partials/TypeBadge';
import { UtilityBadge } from './__partials/UtilityBadge';
import { NftMediaSkeleton } from './Skeleton';
import * as Styled from './styles';
import { PlayIcon } from 'icons/PlayIcon';
import { isVideoOrGifUrl, isVideoUrl } from 'utils';
import {
  IS_FTNFT_AM_MODE,
  IS_KIOSK_AM_MODE,
  IS_KIOSK_VENICE_MODE
} from 'constants/app/configs';
import { ReviewStatus } from 'constants/nfts/blockchains';
import { DisplayMediaTypes, INft, TSizeType } from 'interfaces';

type TNftMedia = {
  nft: INft;
  mediaType?: Exclude<TSizeType, 'huge'>;
  size?: TSizeType;
  scalable?: boolean;
  autoplay?: boolean;
  controls?: boolean;
  className?: string;
  showPreview?: DisplayMediaTypes;
  showSelect?: boolean;
  handleSelectedBulkNft?: (currentNft: INft, isSelected: boolean) => void;
  isSelected?: boolean;
  badges?: {
    sold?: boolean;
    type?: boolean;
    utility?: boolean;
    review?: boolean;
    blockchain?: boolean;
  };
};

export const NftMedia: FC<TNftMedia> = ({
  nft,
  mediaType = 'large',
  size = 'medium',
  scalable,
  autoplay,
  controls,
  className,
  showPreview = DisplayMediaTypes.noPreview,
  badges = {
    sold: true,
    type: true,
    utility: true,
    review: false,
    blockchain: false
  }
}: TNftMedia) => {
  const showBadges = { sold: true, type: true, utility: true, ...badges };
  const isVideo = nft && isVideoUrl(nft.media?.large || '');
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [isPreview, setIsPreview] = useState(true);
  const showNftSold = useMemo(
    () => showBadges.sold && !!nft.sold_at && !nft.is_saleable,
    [nft, showBadges.sold]
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const onLoad = useCallback(() => {
    setMediaLoaded(true);
  }, []);

  const previewImage = useMemo(() => {
    if (
      isVideoOrGifUrl(nft.media?.large || '') &&
      showPreview &&
      nft.preview &&
      typeof nft.preview === 'object' &&
      isPreview
    ) {
      return nft.preview[mediaType || 'large'];
    }
    return null;
  }, [nft.media, nft.preview, showPreview, isPreview]);

  const showPreviewWithButton =
    !!previewImage && showPreview === DisplayMediaTypes.previewWithButton;

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleOpenOriginal = () => {
    window.open(nft?.media?.original, '_blank');
  };

  if (isVideo && !previewImage) {
    return (
      <Styled.VideoWrapper className={className}>
        {badges.blockchain && !isPlaying ? (
          <BlockchainBadge blockchain={nft.blockchain} />
        ) : (
          <OpenOriginalButton onOpenOriginal={handleOpenOriginal} />
        )}
        {!mediaLoaded && <NftMediaSkeleton />}
        <NftVideoMedia
          autoplay={!!autoplay}
          src={nft.media?.[mediaType]}
          controls={!!controls}
          loop
          hover
          poster=""
          onLoad={onLoad}
          onPlay={handlePlay}
          onPause={handlePause}
        />
        {showNftSold && <SoldBadge size={size} />}
        {IS_FTNFT_AM_MODE && nft.utility_product && showBadges.utility && (
          <UtilityBadge size={size} />
        )}
        {IS_FTNFT_AM_MODE &&
          showBadges.review &&
          nft.review_status === ReviewStatus.Pending && (
            <InReviewBadge size={size} />
          )}
        {IS_FTNFT_AM_MODE &&
          showBadges.review &&
          nft.review_status === ReviewStatus.Rejected && (
            <RejectedBadge size={size} />
          )}
        {!(IS_FTNFT_AM_MODE || IS_KIOSK_AM_MODE || IS_KIOSK_VENICE_MODE) &&
          showBadges.type &&
          !isPlaying && <TypeBadge type={nft.creation_type} size={size} />}
      </Styled.VideoWrapper>
    );
  }

  return (
    <Styled.ImageWrapper
      $scalable={!!scalable}
      className={className}
      $showPreviewWithButton={showPreviewWithButton}
    >
      {badges.blockchain && <BlockchainBadge blockchain={nft.blockchain} />}
      {!mediaLoaded && <NftMediaSkeleton />}
      <Styled.Image
        loading="lazy"
        src={previewImage || nft.media?.[mediaType]}
        onLoad={onLoad}
      />
      {showPreviewWithButton && (
        <>
          <Styled.PlayButton onClick={() => setIsPreview(false)}>
            <Styled.IconPre />
            <PlayIcon size={48} />
          </Styled.PlayButton>
          <Styled.ImageOverlay />
        </>
      )}
      {showNftSold && <SoldBadge size={size} />}
      {IS_FTNFT_AM_MODE && nft.utility_product && showBadges.utility && (
        <UtilityBadge size={size} />
      )}
      {IS_FTNFT_AM_MODE &&
        showBadges.review &&
        nft.review_status === ReviewStatus.Pending && (
          <InReviewBadge size={size} />
        )}
      {IS_FTNFT_AM_MODE &&
        showBadges.review &&
        nft.review_status === ReviewStatus.Rejected && (
          <RejectedBadge size={size} />
        )}
      {!(IS_FTNFT_AM_MODE || IS_KIOSK_AM_MODE || IS_KIOSK_VENICE_MODE) &&
        showBadges.type && <TypeBadge type={nft.creation_type} size={size} />}
    </Styled.ImageWrapper>
  );
};
